import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '../shared/alert/alert.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ParamContext } from '../shared/service/param.context';
import { ObservationVariable } from '../shared/model/observation-variable.model';
import { TermIdEnum } from '../shared/ontology/model/termid.enum';
import { VariableTypeEnum } from '../shared/ontology/variable-type.enum';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { DatasetModel } from '../shared/dataset/model/dataset.model';
import { DatasetTypeEnum } from '../shared/dataset/model/dataset-type.enum';
import { DatasetService } from '../shared/dataset/service/dataset.service';
import { AnalyzedTraitModel } from './analyzed-trait.model';
import { MultiSiteAnalysisService } from './multi-site-analysis.service';
import { StudyInstanceModel } from '../shared/dataset/model/study-instance.model';
import { finalize } from 'rxjs/internal/operators/finalize';
import { FileDownloadHelper } from '../entities/sample/file-download.helper';
import { MultiSiteAnalysisInputModel } from './multi-site-analysis-input.model';
import { ObservationUnitsSearchRequest } from '../shared/dataset/model/observation-units-search-request.model';
import { ObservationUnitsSearchResponse } from '../shared/dataset/model/observation-units-search-response.model';
import { JhiEventManager, JhiLanguageService } from '../shared/core-utils';

@Component({
    selector: 'jhi-multi-site-study',
    templateUrl: './multi-site-study.component.html',
})
export class MultiSiteStudyComponent implements OnInit {

    @Input()
        studyName: string;
    @Input()
        studyId: number;
    isDefineScreen = true;

    isLoading = false;

    environmentFactors: ObservationVariable[] = [];
    selectedEnvFactor: ObservationVariable;
    genotypeFactors: ObservationVariable[] = [];
    selectedGenotypeFactor: ObservationVariable;
    groupingFactors: ObservationVariable[] = [];
    selectedGroupingFactor: ObservationVariable;
    analyzedTraits: AnalyzedTraitModel[] = [];
    numberOfInstances: number;
    selectedTraits: any[] = [];
    meansDataset: DatasetModel;
    instances: StudyInstanceModel[] = [];
    instancesMap = new Map<number, ObservationUnitsSearchResponse>();
    selectedInstances: any[] = [];
    adjustedMeansTraits: AnalyzedTraitModel[] = [];
    selectedAdjustedMeansTraits: any[] = [];

    constructor(private activatedRoute: ActivatedRoute,
                private jhiLanguageService: JhiLanguageService,
                private eventManager: JhiEventManager,
                private router: Router,
                private alertService: AlertService,
                private modalService: NgbModal,
                private datasetService: DatasetService,
                public translateService: TranslateService,
                public multiSiteAnalysisService: MultiSiteAnalysisService,
                private fileDownloadHelper: FileDownloadHelper,
                private paramContext: ParamContext
    ) {
    }

    async ngOnInit() {
        this.datasetService.getDatasets(this.studyId, null).subscribe(
            (res: HttpResponse<DatasetModel[]>) => {
                const plotDataset = res.body.filter((dataset: DatasetModel) => dataset.datasetTypeId === DatasetTypeEnum.PLOT)[0];
                const environmentDataset = res.body.filter((dataset: DatasetModel) => dataset.datasetTypeId === DatasetTypeEnum.ENVIRONMENT)[0];
                this.getPlotDatasetVariables(plotDataset.datasetId);
                this.getEnvironmentVariables(environmentDataset.datasetId);
                this.meansDataset = res.body.filter((dataset: DatasetModel) => dataset.datasetTypeId === DatasetTypeEnum.MEANS)[0];
                this.getAnalyzedTraits(this.meansDataset.datasetId);
                this.instances = this.meansDataset.instances;
                this.numberOfInstances = this.instances.length;
                this.getEnvironments(environmentDataset.datasetId, this.meansDataset.instances.map((instance) => instance.instanceId));
            }
        );
    }

    getEnvironments(environmentDatasetId: number, instancesIds: number[]) {
        this.datasetService.getObservationSetColumns(this.studyId, environmentDatasetId).subscribe(
            (res) => {
                const environmentVariables = res.body
                    .filter((variable: ObservationVariable) => variable.variableType?.toString() === VariableTypeEnum[VariableTypeEnum.ENVIRONMENT_DETAIL]);
                const request = new ObservationUnitsSearchRequest();
                request.environmentDetails = environmentVariables;
                request.instanceIds = instancesIds;
                this.datasetService.getObservationUnitTable(this.studyId, environmentDatasetId, request, null).subscribe(
                    (result) => {
                        result.body.forEach((o) => this.instancesMap.set(o.trialInstance, o));
                    }
                );
            }
        );
    }

    getAnalyzedTraits(meansDatasetId: number) {
        this.multiSiteAnalysisService.getAnalyzedTraits(this.studyId, meansDatasetId).subscribe(
            (res) => {
                this.analyzedTraits = res;
            }
        );
    }

    getEnvironmentVariables(environmentDatasetId: number) {
        this.datasetService.getObservationSetColumns(this.studyId, environmentDatasetId).subscribe(
            (res) => {
                this.environmentFactors = res.body
                    .filter((variable: ObservationVariable) => variable.variableType?.toString() === VariableTypeEnum[VariableTypeEnum.ENVIRONMENT_DETAIL]);
                this.selectedEnvFactor = this.environmentFactors.find((factor) => factor.termId === TermIdEnum.TRIAL_INSTANCE);
                this.groupingFactors = this.environmentFactors.filter((factor) => factor.termId !== TermIdEnum.TRIAL_INSTANCE);
            }
        );
    }

    getPlotDatasetVariables(plotDatasetId: number) {
        this.datasetService.getObservationSetColumns(this.studyId, plotDatasetId).subscribe(
            (res: HttpResponse<ObservationVariable[]>) => {
                this.genotypeFactors = res.body.filter((variable) =>
                    variable.variableType?.toString() === VariableTypeEnum[VariableTypeEnum.GERMPLASM_DESCRIPTOR] || variable.termId === TermIdEnum.ENTRY_NO
                );
                this.selectedGenotypeFactor = this.genotypeFactors.find((factor) => factor.termId === TermIdEnum.GID);
            }
        );
    }

    resetDefineScreenValues($event) {
        $event.preventDefault();
        this.selectedEnvFactor = this.environmentFactors.find((factor) => factor.termId === TermIdEnum.TRIAL_INSTANCE);
        this.selectedGenotypeFactor = this.genotypeFactors.find((factor) => factor.termId === TermIdEnum.GID);
        this.selectedTraits = [];
    }

    next($event) {
        $event.preventDefault();
        if (this.validateFields()) {
            this.adjustedMeansTraits = this.analyzedTraits.filter((trait) => this.selectedTraits.includes(trait.termId));
            this.isDefineScreen = false;
            this.resetDownloadScreenValues($event);
        }
    }

    validateFields(): boolean {
        if (!this.selectedEnvFactor) {
            this.alertService.error('multi-site-analysis.validation.environment.factor.required');
            return false;
        }
        if (!this.selectedGenotypeFactor) {
            this.alertService.error('multi-site-analysis.validation.genotype.factor.required');
            return false;
        }
        if (!this.selectedTraits.length) {
            this.alertService.error('multi-site-analysis.validation.trait.required');
            return false;
        }
        return true;
    }

    back($event) {
        $event.preventDefault();
        this.isDefineScreen = true;
    }

    resetDownloadScreenValues($event) {
        $event.preventDefault();
        this.selectedAdjustedMeansTraits = [];
        this.onSelectAllAdjustedMeans();
        this.selectedInstances = [];
        this.onSelectAllInstances();
    }

    download($event) {
        $event.preventDefault();
        if (this.validateInstancesAndTraits()) {
            this.isLoading = true;
            const input = new MultiSiteAnalysisInputModel();
            input.selectedTraitIds = this.selectedAdjustedMeansTraits;
            input.genotypeFactor = this.selectedGenotypeFactor.termId;
            input.environmentGroupingFactor = this.selectedGroupingFactor ? this.selectedGroupingFactor.termId : null;
            input.instanceIds = this.selectedInstances;
            input.environmentFactor = this.selectedEnvFactor.termId;
            this.multiSiteAnalysisService.download(this.studyId, input).pipe(finalize(() => {
                this.isLoading = false;
            })).toPromise().then((response: any) => {
                const fileName = this.fileDownloadHelper.getFileNameFromResponseContentDisposition(response);
                this.fileDownloadHelper.save(response.body, fileName);
            });
        }
    }

    validateInstancesAndTraits(): boolean {
        if (!this.selectedInstances.length) {
            this.alertService.error('multi-site-analysis.validation.environment.required');
            return false;
        }
        if (!this.selectedAdjustedMeansTraits.length) {
            this.alertService.error('multi-site-analysis.validation.trait.required');
            return false;
        }
        return true;
    }

    variableTrackId(index: number, item: AnalyzedTraitModel) {
        return item.termId;
    }

    instanceTrackId(index: number, item: StudyInstanceModel) {
        return item.instanceId;
    }

    isAllInstancesSelected() {
        return this.instances.length > 0 && this.selectedInstances.length === this.instances.length;
    }

    onSelectAllInstances() {
        if (this.isAllInstancesSelected()) {
            this.selectedInstances = [];
        } else {
            this.selectedInstances = [];
            this.instances.forEach((instance) => this.selectedInstances.push(instance.instanceId));
        }
    }

    isInstanceSelected(instance: StudyInstanceModel) {
        return instance && this.selectedInstances.length > 0 && this.selectedInstances.find((item) => item === instance.instanceId);
    }

    getHeritabilityString(trait: AnalyzedTraitModel, instance: StudyInstanceModel): string {
        if (trait.instanceNumberCountMap[instance.instanceId]) {
            return trait.instanceNumberCountMap[instance.instanceId] + '(' + trait.heritabilityMap[instance.instanceNumber] + ')';
        }
        return '0';
    }

    toggleInstanceSelect(instance: StudyInstanceModel) {
        if (this.selectedInstances.length > 0 && this.selectedInstances.find((item) => item === instance.instanceId)) {
            this.selectedInstances = this.selectedInstances.filter((item) => item !== instance.instanceId);
        } else {
            this.selectedInstances.push(instance.instanceId);
        }
    }

    isAllAdjustedMeansSelected() {
        return this.adjustedMeansTraits.length > 0 && this.selectedAdjustedMeansTraits.length === this.adjustedMeansTraits.length;
    }

    onSelectAllAdjustedMeans() {
        if (this.isAllAdjustedMeansSelected()) {
            this.selectedAdjustedMeansTraits = [];
        } else {
            this.selectedAdjustedMeansTraits = [];
            this.adjustedMeansTraits.forEach((trait) => this.selectedAdjustedMeansTraits.push(trait.termId));
        }
    }

    isAdjustedMeanSelected(analyzedTrait: AnalyzedTraitModel) {
        return analyzedTrait && this.selectedAdjustedMeansTraits.length > 0 && this.selectedAdjustedMeansTraits.find((item) => item === analyzedTrait.termId);
    }

    toggleAdjustedMeanSelect(analyzedTraitModel: AnalyzedTraitModel) {
        if (this.selectedAdjustedMeansTraits.length > 0 && this.selectedAdjustedMeansTraits.find((item) => item === analyzedTraitModel.termId)) {
            this.selectedAdjustedMeansTraits = this.selectedAdjustedMeansTraits.filter((item) => item !== analyzedTraitModel.termId);
        } else {
            this.selectedAdjustedMeansTraits.push(analyzedTraitModel.termId);
        }
    }

    isAllAnalyzedTraitsSelected() {
        return this.analyzedTraits.length > 0 && this.selectedTraits.length === this.analyzedTraits.length;
    }

    onSelectAllAnalyzedTraits() {
        if (this.isAllAnalyzedTraitsSelected()) {
            this.selectedTraits = [];
        } else {
            this.selectedTraits = [];
            this.analyzedTraits.forEach((variable) => this.selectedTraits.push(variable.termId));
        }
    }

    isAnalyzedTraitSelected(trait: AnalyzedTraitModel) {
        return trait && this.selectedTraits.length > 0 && this.selectedTraits.find((item) => item === trait.termId);
    }

    toggleTraitSelect(trait: AnalyzedTraitModel) {
        if (this.selectedTraits.length > 0 && this.selectedTraits.find((item) => item === trait.termId)) {
            this.selectedTraits = this.selectedTraits.filter((item) => item !== trait.termId);
        } else {
            this.selectedTraits.push(trait.termId);
        }
    }

    getEnvironmentGroupingFactorText(): string {
        return this.selectedGroupingFactor ? this.selectedGroupingFactor.alias : 'None';
    }

    showEnvironmentFactorColumn(): boolean {
        return this.selectedEnvFactor.termId !== TermIdEnum.TRIAL_INSTANCE;
    }

    showEnvironmentGroupingFactorColumn(): boolean {
        return this.selectedGroupingFactor
            && this.selectedGroupingFactor.termId !== TermIdEnum.TRIAL_INSTANCE
            && this.selectedGroupingFactor.termId !== this.selectedEnvFactor.termId;
    }

    getFactorValue(instanceNumber: number, variable: ObservationVariable): string {
        if (variable) {
            if (variable.termId === TermIdEnum.LOCATION_ID) {
                return this.instancesMap.get(instanceNumber).variables['LOCATION_ID'].value;
            } else if (variable.possibleValues && variable.possibleValues.length > 0) {
                const categoricalIdValue = this.getValue(variable.name,  this.instancesMap.get(instanceNumber));
                for (let i = 0; i < variable.possibleValues.length; i++) {
                    const  valueRef = variable.possibleValues[i];
                    if (valueRef.id.toString() === categoricalIdValue) {
                        return valueRef.name;
                    }
                };
                return categoricalIdValue;
            } else {
                return this.getValue(variable.name,  this.instancesMap.get(instanceNumber));
            }
        }
        return '';
    }

    getValue(key: string, observation: ObservationUnitsSearchResponse): string {
        return observation.environmentVariables[key] ? observation.environmentVariables[key].value : '';
    }

}
